import { batch } from "solid-js";
import { reconcile } from "solid-js/store";
import Cookies from "js-cookie";

import { LocaleCanada } from "@repo/kikoff-models/Locale";
import Promotion, { PromotionCanada } from "@repo/kikoff-models/Promotion";
import User, { UserCanada, UserUsa } from "@repo/kikoff-models/User";
import canadaRpc from "@repo/protobuf/api/canadaRpc";
import { webRpc } from "@repo/protobuf/api/rpc";
import { User as UsUserPb } from "@repo/protobuf/gen/web/public/user_pb";
import Protobuf from "@repo/protobuf/utils";
import { invalidate } from "@repo/ui/global/signals";
import Address from "@repo/utils/Address";
import Chainable from "@repo/utils/Chainable";
import Locale from "@repo/utils/Locale";
import UDate from "@repo/utils/UDate";
import UFunction from "@repo/utils/UFunction";
import UPromise from "@repo/utils/UPromise";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import analytics from "~/analytics/client";

import { useOnboarding } from "./onboarding";

// Only ONBOARDING and ONBOARDED status are in use now
const routeByStatus: Record<User.Status, string> = {
  ONBOARDING: "/signup/info",
  ONBOARDED: "/dashboard",
};

const initialState = {
  authenticated: null as boolean | null,
  status: null as User.Status | null,
  pii: null as User.Pii.Readable | null,
  id: null as User.Id | null,
  impersonating: false,
  promotion: null as Promotion | null,
  expectedLtv: null as number | null,
};

export const { Provider, useUser, Context } = FeatureStore.init(
  "User",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const [onboarding, setOnboarding] = useOnboarding();

    const accessors = {
      authenticated: () => store.authenticated,
      pii: () => store.pii,
      status: Object.assign(
        () => {
          if (onboarding.stage() === "pending_manual_idv_review")
            return "ONBOARDED";
          if (onboarding.stage() === "failed_manual_idv") return "ONBOARDED";
          if (onboarding.stage() === "manual_idv_needs_reupload")
            return "ONBOARDED";

          return store.status;
        },
        {
          route: () => routeByStatus[accessors.status()!],
        }
      ),
      id: () => store.id,
      impersonating: () => store.impersonating,
      promotion: attachAction(
        () => store.promotion,
        () => actions.fetchPromotion()
      ),
      expectedLtv: () => store.expectedLtv,
    };

    const mutations = {
      update(user: User) {
        if (user.locale !== Locale.current) {
          Locale.current = user.locale;
          Cookies.set("locale", user.locale);
          invalidate();
          return;
        }

        analytics.identify(user.id, { email: user.pii.email });

        batch(() => {
          setStore("id", user.id);
          setStore("status", user.status);
          setStore("pii", reconcile(user.pii));
          if (user.expectedLtv)
            setStore("expectedLtv", user.expectedLtv || null);
        });
      },
    };

    const us = (() => {
      const fetch = Chainable.deferrable(
        ({ defer = false }) =>
          () =>
            webRpc.Account.getUserInfo({
              scopes: [UsUserPb.Scope.BASIC_INFO],
            }).then(({ user }) => {
              if (!defer) resolve();
              return resolve;

              function resolve() {
                mutations.update(UserUsa.normalize(user!));
              }
            })
      );

      const actions = {
        init: () =>
          webRpc.Account.getInitPage({
            scopes: [UsUserPb.Scope.BASIC_INFO],
          }).then(({ user }) => {
            if (user) {
              mutations.update(UserUsa.normalize(user!));
              setStore("authenticated", true);
            } else {
              setStore("authenticated", false);
            }
          }),
        fetch,
        create: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              return webRpc.Onboarding.createUser(credentials)
                .CREATED((response) => ({ authenticated: false, response }))
                .AUTHENTICATED((response) => ({
                  authenticated: true,
                  response,
                }))
                .TAKEN(() => {
                  throw new Error(
                    "This email is already in use. Please try another or log in if you already have an account."
                  );
                })
                .elseThrow("Failed to create user.")
                .then(async ({ authenticated, response }) => {
                  // @ts-expect-error .state takes in either us or canada proto, resolves to never
                  setOnboarding.state(response.onboardingStatus!);
                  const resolveUser = await (defer ? fetch.defer() : fetch());
                  return {
                    authenticated,
                    resolveUser() {
                      resolveUser();
                      setStore("authenticated", true);
                    },
                  };
                });
            }
        ),
        login: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              return webRpc.Auth.login(credentials)
                .SUCCESS(({ user }) => {
                  analytics.only("snapchat").convert("SnapchatOnlyLogin");

                  if (!defer) resolve();
                  return resolve;

                  function resolve() {
                    mutations.update(UserUsa.normalize(user!));
                    setStore("authenticated", true);
                  }
                })
                .elseThrow("Failed to login.");
            }
        ),
        logout() {
          setStore("authenticated", null);

          return webRpc.Auth.logout({})
            .SUCCESS(() => {
              analytics.anonymize();

              invalidate();
            })
            .elseThrow("Failed to log out.");
        },
        fetchPromotion: UFunction.todo,
        updateEmail(credentials: User.Credentials) {
          return webRpc.Account.updateEmail(credentials)
            .SUCCESS(() => {
              return actions.init();
            })
            .TAKEN(() => {
              throw new Error(
                "This email is already in use. Please try another or log in if you already have an account."
              );
            })
            .elseThrow("Failed to update email.");
        },
        updatePhone({ phone, password }: { phone: string; password: string }) {
          return (
            webRpc.Account.getPhoneValidationV2({ phone, password })
              .VALID(() => {
                return webRpc.Account.getPhoneCodeV2({
                  phone: phone,
                })
                  .SUCCESS(() => {
                    return actions.init();
                  })
                  .elseThrow("Failed to update phone.");
              })
              // TODO: Add handling for additional mfa states
              .WAITING(() => {})
              .INVALID(() => {})
              .TAKEN(() => {})
              .elseThrow("Failed to update phone.")
          );
        },
        confirmPhone({ phone, code }: { phone: string; code: string }) {
          return webRpc.Auth.updateForgotLoginPii({
            phone,
            phoneCode: code,
          })
            .SUCCESS(() => {
              return actions.init();
            })
            .elseThrow("Failed to confirm phone.");
        },
        updateAddress({
          address,
          password,
        }: {
          address: Address;
          password: string;
        }) {
          return webRpc.Account.updateAddress({ ...address, password })
            .SUCCESS(() => {})
            .elseThrow("Failed to update address.");
        },
        updatePassword({
          oldPassword,
          newPassword,
        }: {
          oldPassword: string;
          newPassword: string;
        }) {
          return webRpc.Account.updatePassword({
            oldPassword,
            newPassword,
            // TODO: Add mfa verification
            mfaVerificationProof: "00000",
          })
            .SUCCESS(() => {})
            .elseThrow("Failed to update password.");
        },
        forgotPassword: UFunction.todo,
        resetPassword: UFunction.todo,
        sendConfirmationEmail: UFunction.todo,
        confirmEmail: UFunction.todo,
        changeLocale(locale: Locale) {
          return webRpc.Account.updateLocale({
            locale,
          }).then(() => actions.init());
        },
      };

      return { actions };
    })();

    const canada = (() => {
      const actions = {
        init() {
          const start = Date.now();
          return canadaRpc.User.bootstrap({}).then(
            ({ user, serverTime, impersonating }) => {
              UDate.GlobalMod.shift(
                Protobuf.Timestamp.toNumber(serverTime!) -
                  Date.now() +
                  (Date.now() - start) / 2
              );

              if (!user) return setStore("authenticated", false);
              batch(() => {
                setStore("authenticated", true);
                setStore("impersonating", impersonating);
                actions.fetchPromotion();
                mutations.update(UserCanada.normalize(user));
              });
            }
          );
        },
        fetch: Chainable.deferrable(
          ({ defer = false }) =>
            () =>
              canadaRpc.User.showUser({}).then(({ user }) => {
                if (!defer) resolve();
                return resolve;

                function resolve() {
                  mutations.update(UserCanada.normalize(user!));
                }
              })
        ),
        create: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              return canadaRpc.Onboarding.createUser({
                ...credentials,
                locale: LocaleCanada.denormalize(Locale.current),
                promotionToken: Cookies.get("utm_content"),
              }).then(async ({ user, onboardingState, authenticated }) => {
                // @ts-expect-error .state takes in either us or canada proto, resolves to never
                setOnboarding.state(onboardingState);
                const resolveUser = () => {
                  mutations.update(UserCanada.normalize(user!));
                  setStore("authenticated", true);
                };
                if (!defer) resolveUser();
                return {
                  authenticated,
                  resolveUser,
                };
              });
            }
        ),
        login: Chainable.deferrable(
          ({ defer = false }) =>
            (credentials: User.Credentials) => {
              return canadaRpc.Auth.login(credentials).then(({ user }) => {
                if (!defer) resolve();
                return resolve;

                function resolve() {
                  mutations.update(UserCanada.normalize(user!));
                  setStore("authenticated", true);
                }
              });
            }
        ),
        fetchPromotion() {
          return canadaRpc.Promotion.userPromotion({}).then(({ promotion }) => {
            if (promotion)
              setStore("promotion", PromotionCanada.normalize(promotion));
          });
        },
        logout() {
          setStore("authenticated", null);
          return canadaRpc.Auth.logout({}).then(() => {
            analytics.anonymize();

            invalidate();
          });
        },
        updateEmail: UFunction.todo,
        updatePhone: UFunction.todo,
        confirmPhone: UFunction.todo,
        updateAddress: UFunction.todo,
        updatePassword: UFunction.todo,
        forgotPassword(email: string) {
          return canadaRpc.Auth.forgotPassword({ email });
        },
        resetPassword(password: string, token: string) {
          return canadaRpc.Auth.resetPassword({
            password,
            confirmationToken: token,
          });
        },
        sendConfirmationEmail() {
          return canadaRpc.User.sendConfirmationEmail({});
        },
        confirmEmail(confirmationToken: string) {
          return canadaRpc.User.confirmEmail({ confirmationToken });
        },
        changeLocale(locale: Locale) {
          return canadaRpc.User.changeLocale({
            locale: LocaleCanada.denormalize(locale),
          }).then(({ user }) => {
            mutations.update(UserCanada.normalize(user!));
          });
        },
      };

      return { actions };
    })();

    const actions = withLogger("action", {
      async pollEmailVerified() {
        await actions.fetch();
        if (!store.pii?.emailConfirmed) {
          await UPromise.delay(1000);
          await actions.pollEmailVerified();
        } else await setOnboarding.fetch();
      },
      ...(import.meta.env.API_ADAPTER === "canada"
        ? canada.actions
        : us.actions),
    });

    return { accessors, actions };
  }
);
